<template>
  <div class="row">
    <div class="col-12">
      <filters v-model="filter" :filter-config="filterConfig" />
    </div>
    <div class="col-12">
      <b-table
        id="device-table"
        ref="table"
        table-class="table-filtered"
        :fields="fields"
        :items="devices"
        :responsive="true"
        :sticky-header="true"
        :busy="isLoading"
        :per-page="perPage"
        :current-page="currentPage"
        :filter="filter"
        :filter-function="filterRows"
        :sort-by="'createdAt'"
        :sort-desc="true"
        :label-sort-asc="sortAscTitle"
        :label-sort-desc="sortDescTitle"
        fixed
        show-empty
        @filtered="onFiltered"
      >
        <template v-slot:head(check)>
          <b-form-checkbox id="checkbox__all" v-model="allSelected" :checked="allSelected" name="checkbox__all" @change="toggleSelectAll" />
        </template>
        <template v-slot:cell(check)="row">
          <b-form-checkbox
            :id="'checkbox__' + row.item.id"
            v-model="row.item.isSelected"
            :checked="row.item.isSelected || allSelected"
            :name="'checkbox__' + row.item.id"
          />
        </template>
        <template v-slot:head(actions)>
          <b-button v-if="isSuperadmin" v-b-modal.createDevice variant="primary" size="sm"> <BIconPlus />{{ 'new' | i18n }} </b-button>
          <b-dropdown variant="outline-primary" right class="actions-dropdown dropdown" :disabled="!isSelectedRows" size="sm">
            <b-dropdown-item v-b-modal.sendNotifictation>
              {{ 'send_notification' | i18n }}
            </b-dropdown-item>
          </b-dropdown>
        </template>
        <template v-slot:cell(createdAt)="row">
          {{ row.item.createdAt | formatDate('DD.MM.YY HH:mm') }}
        </template>
        <template v-slot:cell(actions)="row">
          <div class="actions-wrapper">
            <span v-b-modal.sendNotifictation class="btn-edit-device" @click="sendNotificationToSingl(row.item)">
              <BIconEnvelope v-b-tooltip.hover font-scale="1.2" :title="'send' | i18n" />
            </span>
            <span v-b-modal.editDevice class="btn-edit-device" @click="setEditDevice(row.item)">
              <BIconPencil v-b-tooltip.hover font-scale="1.2" :title="'edit' | i18n" />
            </span>
            <span v-if="isSuperadmin" class="btn-edit-device" @click="deleteDevice(row.item)">
              <BIconTrash v-b-tooltip.hover font-scale="1.2" :title="'delete' | i18n" />
            </span>
          </div>
        </template>
        <template v-slot:table-busy>
          <div class="text-center">
            <b-spinner class="align-middle" />
            <strong class="ml-1">{{ 'loading' | i18n }}...</strong>
          </div>
        </template>
      </b-table>
      <b-pagination v-model="currentPage" class="table-pagination" :total-rows="totalRows" :per-page="perPage" size="sm" />
    </div>
    <send-notification :selected-rows="selectedRows" />
    <edit-device :device="editDevice" :update="getDevices" />
    <create-device :update="getDevices" />
  </div>
</template>

<script>
import { DeviceMixin, NotificationMixin, TableFilterMixin, ScreenSizeMixin, DeleteRowMixin, AuthMixin } from '../../../mixins';
import { SORT_ASC_TITLE, SORT_DESC_TITLE } from '../../../../../../constants';
import Filters from '../../table/filters';
import { BTable, BIconPlus, BIconPencil, BIconTrash, BIconEnvelope } from 'bootstrap-vue';

import SendNotification from './modals/sendNotification';
import EditDevice from './modals/editDevice';
import CreateDevice from './modals/createDevice';

export default {
  name: 'Devices',
  components: { BTable, BIconPlus, BIconPencil, BIconTrash, BIconEnvelope, Filters, SendNotification, EditDevice, CreateDevice },
  mixins: [DeviceMixin, NotificationMixin, TableFilterMixin, ScreenSizeMixin, DeleteRowMixin, AuthMixin],
  data: function() {
    return {
      devices: [],
      fields: [
        { key: 'check', label: '', sortable: false, thClass: 'width-5' },
        { key: 'deviceNumber', label: this.$translate('number_short'), sortable: true, thClass: 'width-10' },
        { key: 'deviceId', label: 'App-Id', sortable: true, thClass: 'width-15' },
        { key: 'belongsToEmployee', label: this.$translate('employee'), sortable: true, thClass: 'width-20' },
        { key: 'notes', label: this.$translate('device_notes'), sortable: true, thClass: 'width-20' },
        { key: 'createdAt', label: this.$translate('created_date'), sortable: true, thClass: 'width-15' },
        { key: 'notificationToken', label: this.$translate('notification_token'), sortable: true, thClass: 'width-15' },
        { key: 'actions', label: '', thClass: ['width-15', 'actions'], tdClass: 'actions' }
      ],
      filterConfig: {
        mainFilters: [
          { key: 'deviceNumber', label: 'number_short' },
          { key: 'deviceId', label: 'App-Id' },
          { key: 'belongsToEmployee', label: 'employee' },
          { key: 'notes', label: 'device_notes' }
        ]
      },
      isLoading: false,
      perPage: 10,
      currentPage: 1,
      totalRows: 0,
      allSelected: false,
      showNewRow: false,
      filter: {
        deviceNumber: null,
        deviceId: null,
        belongsToEmployee: null,
        notes: null
      },
      newDevice: {
        deviceNumber: null,
        deviceId: null,
        notes: null,
        belongsToEmployee: null
      },
      editDevice: {},
      sortAscTitle: this.$translate(SORT_ASC_TITLE),
      sortDescTitle: this.$translate(SORT_DESC_TITLE)
    };
  },
  computed: {
    selectedRows: function() {
      return this.devices.filter(r => r.isSelected);
    },
    isSelectedRows: function() {
      return Boolean(this.selectedRows.length);
    }
  },
  created() {
    if (this.isExtraLargeScreen) {
      this.perPage = 20;
    }
    this.getDevices();
  },
  methods: {
    setEditDevice(item) {
      this.editDevice = Object.assign({}, item);
    },
    async getDevices() {
      this.isLoading = true;
      const response = await this.deviceService.getDevices({});
      this.devices = response.devices.map(d => ({
        id: d.id,
        deviceNumber: d.deviceNumber,
        deviceId: d.deviceId,
        notes: d.notes,
        belongsToEmployee: d.belongsToEmployee,
        createdAt: d.createdAt,
        notificationToken: d.notificationToken ? `${d.notificationToken.slice(0, 15)}...` : '',
        isSelected: false
      }));
      this.totalRows = this.devices.length;
      this.isLoading = false;
    },
    async deleteDevice(device) {
      await this.deleteSelectedRows(
        {
          message: this.$translate('device_delete_confirmation_message'),
          rowDisplayValues: [device.deviceId]
        },
        async () => {
          await this.deviceService.deleteDevices({ ids: [device.id] });
          await this.getDevices();
        }
      );
    },
    sendNotificationToSingl(item) {
      this.devices.forEach(r => {
        r.isSelected = item.id === r.id;
      });
    },
    toggleSelectAll() {
      this.allSelected = !this.allSelected;
      this.devices.forEach(u => (u.isSelected = this.allSelected));
    },
    onFiltered(filteredItems) {
      this.totalRows = filteredItems.length;
      this.currentPage = 1;
    }
  }
};
</script>

<style scoped lang="scss">
.main-content {
  padding: 0;
}

.btn-edit-device {
  min-width: 30px;
  height: 50px;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
}

.btn-edit-device:hover {
  color: #2260ff;
}

.page-header {
  h1 {
    margin-bottom: 0;
  }
}

.b-table-sticky-header {
  max-height: calc(100vh - 400px);
  min-height: calc(100vh - 400px);
}

::v-deep   .table {
  min-width: 1000px;
  white-space: normal;
  text-overflow: ellipsis;
  overflow: hidden;

  td,
  th {
    padding: 8px 14px !important;
  }

  th {
    &.width-15 {
      width: 15%;
    }

    &.width-25 {
      width: 25%;
    }

    &.width-12 {
      width: 12%;
    }

    &.width-10 {
      width: 10%;
    }

    &.width-5 {
      width: 5%;
    }
  }

  th.actions,
  td.actions {
    text-align: center;
  }

  th.actions {
    vertical-align: bottom;
  }

  button {
    width: 80px;
  }
}

.owner-wrapper {
  display: flex;
  flex-wrap: wrap;
}

.owner-block {
  height: 60px;
  display: flex;
  justify-content: center;
  border-radius: 0.2rem;
  box-shadow: 0px 0px 17px -7px rgba(0, 0, 0, 0.35);
  width: fit-content;
  padding: 10px;
  margin: 10px;
  flex-direction: column;
}

.dropdown {
  width: 20px;
  margin-left: 10px;
}

.actions-wrapper {
  display: flex;
  justify-content: center;
  align-items: center;
}
.actions-wrapper > button {
  margin-right: 5px;
}
::v-deep   .filter-wrapper:first-child {
  padding-left: 0;
}
</style>
